
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingActualDispatchService from "../services/tradingActualDispatchService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: {
    margin: "7px 0 0",
    color: "#747b8d",
    fontSize: "13px",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "9px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "18px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "13px",
    marginBottom: "16px",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    minHeight: "93px",
    boxSizing: "border-box",
    borderRadius: "12px",
    padding: "20px 16px",
    color: "#fff",
    boxShadow: "0 5px 12px rgba(15, 23, 42, 0.10)",
  },
  statTotal: {
    background: "linear-gradient(115deg, #19a7f5 0%, #0860e8 100%)",
  },
  statDraft: {
    background: "linear-gradient(115deg, #a47bff 0%, #7738df 100%)",
  },
  statConfirmed: {
    background: "linear-gradient(115deg, #39c985 0%, #159957 100%)",
  },
  statCancelled: {
    background: "linear-gradient(115deg, #ffb51b 0%, #ff8500 100%)",
  },
  statIcon: {
    width: "46px",
    height: "46px",
    minWidth: "46px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.23)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
  },
  statLabel: {
    color: "#fff",
    fontSize: "11px",
    fontWeight: 700,
    marginBottom: "7px",
  },
  statValue: {
    fontSize: "27px",
    fontWeight: 750,
    lineHeight: 1.1,
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  input: {
    minWidth: "220px",
    flex: 1,
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "11px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    minWidth: "170px",
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 12px",
    background: "#fff",
    fontSize: "13px",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1050px",
  },
  th: {
    textAlign: "left",
    padding: "13px 12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
  },
  link: {
    color: "#5548cb",
    fontWeight: 700,
    cursor: "pointer",
    border: 0,
    padding: 0,
    background: "transparent",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: {
    color: "#858b9b",
    fontSize: "13px",
    textAlign: "center",
    padding: "32px 12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "15px",
    color: "#747b8d",
    fontSize: "12px",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

const normalizeStatus = (value) =>
  String(value || "DRAFT").trim().toUpperCase();

const formatStatus = (value) =>
  normalizeStatus(value).replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const formatQuantity = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const statusColors = {
  DRAFT: { background: "#efedff", color: "#5548cb" },
  CONFIRMED: { background: "#dcfce7", color: "#166534" },
  CANCELLED: { background: "#fee2e2", color: "#b42318" },
};

function StatusBadge({ status }) {
  const colors = statusColors[normalizeStatus(status)] || {
    background: "#f1f3f9",
    color: "#5b6172",
  };

  return (
    <span
      style={{
        display: "inline-block",
        borderRadius: "20px",
        padding: "5px 9px",
        fontSize: "11px",
        fontWeight: 700,
        whiteSpace: "nowrap",
        ...colors,
      }}
    >
      {formatStatus(status)}
    </span>
  );
}

export default function ActualDispatchListPage() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    per_page: 20,
  });

  const loadActualDispatches = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (statusFilter !== "ALL") {
        params.status_code = statusFilter;
      }

      const response =
        await tradingActualDispatchService.getActualDispatchList(params);

      const body = getResponseData(response);

      setRecords(Array.isArray(body.items) ? body.items : []);

      setPagination({
        total: Number(body.total || 0),
        pages: Number(body.pages || 0),
        per_page: Number(body.per_page || 20),
      });
    } catch (err) {
      setRecords([]);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Actual Dispatch records."
      );
    } finally {
      setLoading(false);
    }
  }, [page, pagination.per_page, search, statusFilter]);

  useEffect(() => {
    loadActualDispatches();
  }, [loadActualDispatches]);

  const changeSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const changeStatus = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const openDetail = (row) => {
    if (row?.id) {
      navigate(`/trading/actual-dispatches/${row.id}`);
    }
  };

  const statCards = [
    {
      label: "Total Records",
      value: pagination.total,
      icon: "fas fa-truck",
      accent: styles.statTotal,
    },
    {
      label: "Draft",
      value: records.filter(
        (row) => normalizeStatus(row.status_code) === "DRAFT"
      ).length,
      icon: "fas fa-file-alt",
      accent: styles.statDraft,
    },
    {
      label: "Confirmed",
      value: records.filter(
        (row) => normalizeStatus(row.status_code) === "CONFIRMED"
      ).length,
      icon: "fas fa-check-circle",
      accent: styles.statConfirmed,
    },
    {
      label: "Cancelled",
      value: records.filter(
        (row) => normalizeStatus(row.status_code) === "CANCELLED"
      ).length,
      icon: "fas fa-ban",
      accent: styles.statCancelled,
    },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Trading Actual Dispatch</h1>
          <p style={styles.subtitle}>
            Record physical shipments against approved Dispatch Orders and
            track Delivery Challans.
          </p>
        </div>

        <button
          type="button"
          style={styles.button}
          onClick={() =>
            navigate("/trading/actual-dispatches/create")
          }
        >
          + Create Actual Dispatch
        </button>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
          <button
            type="button"
            onClick={loadActualDispatches}
            style={{
              marginLeft: "12px",
              border: 0,
              background: "transparent",
              color: "#b42318",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.stats}>
        {statCards.map((card) => (
          <div
            key={card.label}
            style={{ ...styles.statCard, ...card.accent }}
          >
            <div style={styles.statIcon}>
              <i className={card.icon} />
            </div>
            <div>
              <div style={styles.statLabel}>{card.label}</div>
              <div style={styles.statValue}>{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => changeSearch(event.target.value)}
            placeholder="Search Actual Dispatch no., Sales Order or Customer..."
            style={styles.input}
            aria-label="Search Actual Dispatch"
          />

          <select
            value={statusFilter}
            onChange={(event) => changeStatus(event.target.value)}
            style={styles.select}
            aria-label="Filter Actual Dispatch by status"
          >
            <option value="ALL">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <button
            type="button"
            style={{
              ...styles.button,
              opacity: loading ? 0.65 : 1,
            }}
            onClick={loadActualDispatches}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Actual Dispatch No.</th>
                <th style={styles.th}>Dispatch Order No.</th>
                <th style={styles.th}>Sales Order No.</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Dispatch Date</th>
                <th style={styles.th}>Items</th>
                <th style={styles.th}>Quantity</th>
                <th style={styles.th}>Delivery Challan</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={styles.muted}>
                    Loading Actual Dispatch records...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={10} style={styles.muted}>
                    {error
                      ? "Actual Dispatch records could not be loaded."
                      : "No Actual Dispatch records found."}
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openDetail(row)}
                      >
                        {row.actual_dispatch_no || `AD-${row.id}`}
                      </button>
                    </td>

                    <td style={styles.td}>
                      {row.dispatch_order_no || "—"}
                    </td>

                    <td style={styles.td}>
                      {row.sales_order_no || "—"}
                    </td>

                    <td style={styles.td}>
                      {row.customer_name || "—"}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.actual_dispatch_date)}
                    </td>

                    <td style={styles.td}>
                      {row.total_items ?? 0}
                    </td>

                    <td style={styles.td}>
                      {formatQuantity(row.total_quantity)}
                    </td>

                    <td style={styles.td}>
                      {row.delivery_challan_no || "—"}
                    </td>

                    <td style={styles.td}>
                      <StatusBadge status={row.status_code} />
                    </td>

                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openDetail(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <span>
            Showing {records.length} of {pagination.total} Actual Dispatch
            record{pagination.total === 1 ? "" : "s"}
          </span>

          <div style={styles.pagination}>
            <button
              type="button"
              style={styles.secondaryButton}
              disabled={loading || page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>

            <span>
              Page {page} of {Math.max(1, pagination.pages)}
            </span>

            <button
              type="button"
              style={styles.secondaryButton}
              disabled={loading || page >= pagination.pages}
              onClick={() =>
                setPage((current) =>
                  Math.min(pagination.pages, current + 1)
                )
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
